import React from 'react';
import { Info, ShieldCheck } from 'lucide-react';

const gbp = (n, cur = 'GBP') => `${cur === 'GBP' ? '£' : cur + ' '}${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const utc = (iso) => (iso ? `${String(iso).slice(0, 16).replace('T', ' ')} (UTC+0)` : null);

/** Supplier cancellation policy (ETG `cancellation_penalties`, shown unmodified, UTC+0) + hotel important info (metapolicy). */
export const HotelPolicyInfo = ({ hotel, testIdPrefix }) => {
  const policies = hotel.cancellation_policies || [];
  const info = hotel.important_info || {};
  const extra = (info.metapolicy_extra_info || '').trim();
  const struct = info.metapolicy_struct || {};
  const lines = [];
  (struct.check_in_check_out || []).forEach((p) => lines.push(`${p.check_in_check_out_type?.replace(/_/g, ' ') || 'Check-in/out'}: ${p.inclusion || ''} ${p.price ? `${p.currency || ''} ${p.price}` : ''}`.trim()));
  (struct.children || []).forEach((p) => lines.push(`Children: ${p.age_start ?? ''}–${p.age_end ?? ''} yrs ${p.extra_bed || ''} ${p.inclusion || ''}`.replace(/\s+/g, ' ').trim()));
  (struct.deposit || []).forEach((p) => lines.push(`Deposit: ${p.deposit_type?.replace(/_/g, ' ') || ''} ${p.price ? `${p.currency || ''} ${p.price}` : ''} ${p.inclusion || ''}`.replace(/\s+/g, ' ').trim()));
  (struct.visa || []).forEach((p) => lines.push(`Visa support: ${p.visa_support || ''}`));
  (struct.no_show || []).forEach((p) => lines.push(`No-show: ${p.availability || ''} ${p.time || ''}`.trim()));
  if (!policies.length && !lines.length && !extra) return null;
  return (
    <div className="space-y-2 text-xs" data-testid={`${testIdPrefix}-policy`}>
      {policies.length > 0 && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5">
          <div className="font-bold text-slate-700 flex items-center gap-1 mb-1"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />Cancellation policy (supplier terms)</div>
          <ul className="space-y-0.5 text-slate-600" data-testid={`${testIdPrefix}-cancellation-policies`}>
            {hotel.free_cancellation_before && <li>Free cancellation until {utc(hotel.free_cancellation_before)}</li>}
            {policies.map((p, i) => (
              <li key={i}>{p.start_at ? `From ${utc(p.start_at)}` : 'From booking'}{p.end_at ? ` until ${utc(p.end_at)}` : ''}: {Number(p.amount_show ?? p.amount_charge ?? 0) > 0 ? `penalty ${gbp(p.amount_show ?? p.amount_charge, p.currency_code)}` : 'free cancellation'}</li>
            ))}
          </ul>
        </div>
      )}
      {(lines.length > 0 || extra) && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5">
          <div className="font-bold text-amber-900 flex items-center gap-1 mb-1"><Info className="h-3.5 w-3.5" />Important hotel information</div>
          <ul className="space-y-0.5 text-amber-900" data-testid={`${testIdPrefix}-important-info`}>
            {lines.map((l, i) => <li key={i}>{l}</li>)}
            {extra && <li className="whitespace-pre-line">{extra.replace(/<[^>]+>/g, '')}</li>}
          </ul>
        </div>
      )}
    </div>
  );
};
